/**
 * ServiceCatalogHub — Feb 2026 (refactored for User request E + follow-up)
 * =======================================================================
 *
 * Reusable CRUD hub for the main/sub service pickers used inside the
 * manual-entry forms. Backed by /api/admin/service-catalog/{scope}.
 *
 * Mounted from THREE places:
 *   1. Customer manual-entry catalog
 *      /control/cloudgm/customer-dropdowns/manual-entry           scope="customer"
 *   2. Agent manual-task catalog (shown inside customer hub)
 *      /control/cloudgm/customer-dropdowns/manual-tasks           scope="agent"
 *   3. Legacy merchant dropdowns page
 *      /control/cloudgm-business/merchant-dropdowns               scope="agent"
 *
 * The Feb 2026 rules (per user):
 *   • Add-main dialog collects at least ONE sub-service inline (names only).
 *   • Sub-services no longer carry a price — pricing lives entirely inside
 *     the manual-task form (gov procedures + service fee + fee_model radio).
 *   • The last sub-service of a main service cannot be deleted.
 *   • The translate button is BIDIRECTIONAL (Ar↔En) — write in either field
 *     and translate to the other.
 *   • The old "third language" section is removed.
 */
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Edit3, Trash2, ChevronDown, ChevronRight,
  Loader2, ListChecks, X, Check, Bot, ArrowLeftRight,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import DashboardHeader from '../components/DashboardHeader';
import BreadcrumbNav from '../components/BreadcrumbNav';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem('admin_token') || localStorage.getItem('admin_token') || ''}`,
  'Content-Type': 'application/json',
});

// ── main component ────────────────────────────────────────────────────

export default function ServiceCatalogHub({ scope, contextArea, backPath }) {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const isCustomer = scope === 'customer';
  const isCompany  = scope === 'company';
  const accent     = isCustomer ? 'text-cyan-400'
                    : isCompany  ? 'text-emerald-400'
                    :              'text-purple-400';
  const accentBg   = isCustomer ? 'bg-cyan-500/10'
                    : isCompany  ? 'bg-emerald-500/10'
                    :              'bg-purple-500/10';
  const accentBd   = isCustomer ? 'border-cyan-500/40'
                    : isCompany  ? 'border-emerald-500/40'
                    :              'border-purple-500/40';
  const accentHex  = isCustomer ? '#22d3ee'
                    : isCompany  ? '#34d399'
                    :              '#a78bfa';

  // iter-218n (Feb 2026) — When opened from the merchant control's
  // dropdown-lists sub-hub, the back button should land back on that
  // sub-hub view (?view=dropdown-lists), not the main merchant page.
  const resolvedBackPath =
    backPath
    ?? (isCustomer
          ? '/control/cloudgm'
          : '/control/cloudgm-business?view=dropdown-lists');

  const breadcrumb = buildBreadcrumb({ isCustomer, isCompany, contextArea });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIds, setOpenIds] = useState({});
  const [dialog, setDialog] = useState(null);
  // { mode: 'main-add' | 'main-edit' | 'sub-add' | 'sub-edit', ... }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API_URL}/api/admin/service-catalog/${scope}`,
        { headers: getHeaders() },
      );
      setRows(r.data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل التحميل', 'Load failed'));
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  // iter-218n (Feb 2026) — Merchant-side title/subtitle changed per user:
  // "الخدمات الرئيسية و الفرعية" instead of the older "مسمى الخدمة لإدخال…".
  // iter-248 (Feb 2026) — New `company` scope for company-file services.
  const titleAr = isCustomer
    ? (contextArea === 'customer' ? 'مسمى الخدمة للإدخال اليدوي' : 'القوائم المنسدلة للتحكم بمنصة العملاء')
    : isCompany
    ? 'قوائم خدمات الشركات لملفات الشركات'
    : 'الخدمات الرئيسية و الفرعية';
  const titleEn = isCustomer
    ? (contextArea === 'customer' ? 'Manual Entry Service Name' : 'Customer Platform Dropdown Lists')
    : isCompany
    ? 'Company Services Lists for Company Files'
    : 'Main & Sub Services';

  const openAddMain = () => setDialog({
    mode: 'main-add',
    name_ar: '', name_en: '',
    subs: [emptySubDraft()],   // MUST have at least one sub row on submit.
  });

  const openEditMain = (row) => setDialog({
    mode: 'main-edit',
    id: row.id,
    name_ar: row.name_ar || '',
    name_en: row.name_en || '',
  });

  const openAddSub = (row) => setDialog({
    mode: 'sub-add',
    main_id: row.id,
    main_name: row.name_ar,
    name_ar: '', name_en: '',
  });

  const openEditSub = (row, sub) => setDialog({
    mode: 'sub-edit',
    main_id: row.id,
    main_name: row.name_ar,
    id: sub.id,
    name_ar: sub.name_ar || '',
    name_en: sub.name_en || '',
  });

  const deleteMain = async (row) => {
    if (!window.confirm(t(`حذف "${row.name_ar}" وكل خدماتها الفرعية؟`,
                          `Delete "${row.name_ar}" and all its sub-services?`))) return;
    try {
      await axios.delete(`${API_URL}/api/admin/service-catalog/${scope}/${row.id}`, { headers: getHeaders() });
      toast.success(t('تم الحذف', 'Deleted'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحذف', 'Delete failed'));
    }
  };

  const deleteSub = async (row, sub) => {
    if ((row.sub_services || []).length <= 1) {
      toast.error(t(
        'لا يمكن حذف الخدمة الفرعية الأخيرة — يجب إبقاء واحدة على الأقل، أو احذف الخدمة الرئيسية بالكامل.',
        'Cannot delete the last sub-service — keep at least one, or delete the whole main service.'));
      return;
    }
    if (!window.confirm(t(`حذف "${sub.name_ar}"؟`, `Delete "${sub.name_ar}"?`))) return;
    try {
      await axios.delete(`${API_URL}/api/admin/service-catalog/${scope}/${row.id}/sub/${sub.id}`, { headers: getHeaders() });
      toast.success(t('تم الحذف', 'Deleted'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحذف', 'Delete failed'));
    }
  };

  return (
    <div className="min-h-screen bg-[#030303]" dir={isAr ? 'rtl' : 'ltr'}>
      <DashboardHeader showBackButton backPath={resolvedBackPath} />

      <div className="border-b border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            ...breadcrumb,
            { label_ar: titleAr, label_en: titleEn, path: null },
          ]} />
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className={`w-14 h-14 rounded-xl ${accentBg} border ${accentBd} flex items-center justify-center flex-shrink-0`}>
              <ListChecks className={`w-7 h-7 ${accent}`} />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">
                {t(titleAr, titleEn)}
              </h1>
              <p className="text-sm text-white/50 mt-1 max-w-2xl">
                {t(
                  isCustomer
                    ? 'أدر مسميات الخدمات (رئيسي + فرعي) اللي تظهر في فورم "إضافة طلب لعميل (يدوي)".'
                    : isCompany
                    ? 'أدر مسميات الخدمات (رئيسي + فرعي) اللي تظهر في زر "إضافة خدمة" داخل ملف الشركة.'
                    : 'أدر مسميات الخدمات (رئيسي + فرعي) اللي تظهر في فورم "إدراج مهمة لوكيل (يدوي)".',
                  isCustomer
                    ? 'Manage service names (main + sub) shown in the "Add manual customer request" form.'
                    : isCompany
                    ? 'Manage service names (main + sub) shown in the "Add Service" button inside a company file.'
                    : 'Manage service names (main + sub) shown in the "Add manual agent task" form.',
                )}
              </p>
            </div>
          </div>
          <Button
            onClick={openAddMain}
            className="text-black font-bold gap-2 shrink-0"
            style={{ backgroundColor: accentHex }}
            data-testid="btn-add-main">
            <Plus className="w-4 h-4" />
            {t('إضافة خدمة رئيسية', 'Add main service')}
          </Button>
        </div>

        {loading ? (
          <div className="p-16 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-white/40 mx-auto" />
          </div>
        ) : rows.length === 0 ? (
          <div className="p-16 text-center bg-white/[0.02] border border-dashed border-white/10 rounded-2xl">
            <ListChecks className="w-12 h-12 text-white/20 mx-auto mb-2" />
            <p className="text-white/50 text-sm">
              {t('لا توجد خدمات في هذه القائمة بعد.', 'No services in this catalog yet.')}
            </p>
            <p className="text-white/30 text-[11px] mt-1">
              {t('ابدأ بإضافة الخدمة الرئيسية من الزر أعلاه.', 'Start by adding a main service from the button above.')}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((row) => (
              <MainCard
                key={row.id}
                row={row}
                open={!!openIds[row.id]}
                toggle={() => setOpenIds((o) => ({ ...o, [row.id]: !o[row.id] }))}
                onEdit={() => openEditMain(row)}
                onDelete={() => deleteMain(row)}
                onAddSub={() => openAddSub(row)}
                onEditSub={(sub) => openEditSub(row, sub)}
                onDeleteSub={(sub) => deleteSub(row, sub)}
                t={t} isAr={isAr} accent={accent}
              />
            ))}
          </div>
        )}
      </main>

      <AnimatePresence>
        {dialog && (
          <ServiceDialog
            scope={scope}
            dialog={dialog}
            onClose={() => setDialog(null)}
            onDone={() => { setDialog(null); load(); }}
            t={t} isAr={isAr}
            accent={accentHex}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function buildBreadcrumb({ isCustomer, isCompany, contextArea }) {
  const dashboard = { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' };
  const fzlc      = { label_ar: 'CLOUDGM FZ LLC (للتحكم)', label_en: 'CLOUDGM FZ LLC (Control)', path: '/dashboard?view=platform-control&sub=fzlc' };

  if (contextArea === 'customer' || isCustomer) {
    return [
      dashboard, fzlc,
      { label_ar: 'التحكم والمعلومات (منصة العملاء)', label_en: 'Control & Info (Customers)', path: '/control/cloudgm' },
      { label_ar: 'القوائم المنسدلة للتحكم بمنصة العملاء', label_en: 'Customer Platform Dropdown Lists', path: '/control/cloudgm/customer-dropdowns' },
    ];
  }
  // iter-248 (Feb 2026) — Company-scope catalog opens from the merchant
  // dropdown-lists sub-hub, but its title/testids identify it as the
  // company-file services list.
  // iter-218n (Feb 2026) — Include the dropdown-lists sub-hub as an
  // intermediate crumb so the top-nav reflects the actual navigation
  // path: Dashboard → CloudGM FZ LLC → Merchants → Dropdown Lists → this page.
  return [
    dashboard, fzlc,
    { label_ar: 'التحكم والمعلومات (منصة التجار)', label_en: 'Control & Info (Merchants)', path: '/control/cloudgm-business' },
    { label_ar: 'القوائم المنسدلة للتحكم بمنصة التجار', label_en: 'Merchant Platform Dropdown Lists', path: '/control/cloudgm-business?view=dropdown-lists' },
  ];
}

function emptySubDraft() {
  return { name_ar: '', name_en: '' };
}

// ── Main card ─────────────────────────────────────────────────────────

function MainCard({ row, open, toggle, onEdit, onDelete, onAddSub, onEditSub, onDeleteSub, t, isAr, accent }) {
  const subs = row.sub_services || [];
  return (
    <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden"
         data-testid={`main-card-${row.id}`}>
      <div className="p-4 flex items-center gap-3">
        <button onClick={toggle}
                className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center text-white/60"
                data-testid={`toggle-${row.id}`}>
          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className={`w-4 h-4 ${isAr ? 'rotate-180' : ''}`} />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-white font-semibold text-sm truncate">
            {isAr ? row.name_ar : (row.name_en || row.name_ar)}
          </div>
          <div className="text-white/40 text-[11px] mt-0.5">
            {isAr ? row.name_en : row.name_ar}
          </div>
        </div>
        <span className="text-[11px] px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-white/60">
          {subs.length} {t('فرعية', 'sub')}
        </span>
        <button onClick={onAddSub}
                className={`h-8 px-3 rounded-lg bg-white/5 hover:bg-white/10 text-xs flex items-center gap-1 ${accent}`}
                data-testid={`add-sub-${row.id}`}>
          <Plus className="w-3 h-3" />
          {t('فرعية', 'Sub')}
        </button>
        <button onClick={onEdit}
                className="h-8 w-8 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 flex items-center justify-center"
                data-testid={`edit-main-${row.id}`}>
          <Edit3 className="w-3.5 h-3.5" />
        </button>
        <button onClick={onDelete}
                className="h-8 w-8 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 flex items-center justify-center"
                data-testid={`delete-main-${row.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      {open && subs.length > 0 && (
        <div className="border-t border-white/5 bg-black/20">
          {subs.map((s) => {
            const isLast = subs.length <= 1;
            return (
              <div key={s.id}
                   className="px-4 py-2.5 flex items-center gap-3 border-b border-white/[0.03] last:border-b-0"
                   data-testid={`sub-row-${s.id}`}>
                <span className="w-1 h-6 bg-white/10 rounded" />
                <div className="flex-1 min-w-0">
                  <div className="text-white/80 text-sm truncate">
                    {isAr ? s.name_ar : (s.name_en || s.name_ar)}
                  </div>
                  <div className="text-white/30 text-[11px]">
                    {isAr ? s.name_en : s.name_ar}
                  </div>
                </div>
                <button onClick={() => onEditSub(s)}
                        className="h-7 w-7 rounded-md bg-white/5 hover:bg-white/10 text-white/60 flex items-center justify-center"
                        data-testid={`edit-sub-${s.id}`}>
                  <Edit3 className="w-3 h-3" />
                </button>
                <button onClick={() => onDeleteSub(s)}
                        disabled={isLast}
                        title={isLast ? t('لا يمكن حذف الخدمة الفرعية الأخيرة', 'Cannot delete the last sub-service') : ''}
                        className={`h-7 w-7 rounded-md flex items-center justify-center ${isLast ? 'bg-white/[0.03] text-white/20 cursor-not-allowed' : 'bg-red-500/10 hover:bg-red-500/20 text-red-400'}`}
                        data-testid={`delete-sub-${s.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}
      {open && subs.length === 0 && (
        <div className="border-t border-white/5 bg-black/20 px-4 py-4 text-center text-white/30 text-xs">
          {t('لا توجد خدمات فرعية — أضف من الزر بجانب "فرعية"',
              'No sub-services — add one with the "Sub" button')}
        </div>
      )}
    </div>
  );
}

function ServiceDialog({ scope, dialog, onClose, onDone, t, isAr, accent }) {
  const isSub  = dialog.mode.startsWith('sub-');
  const isEdit = dialog.mode.endsWith('-edit');

  // Local form state
  const [nameAr, setNameAr] = useState(dialog.name_ar || '');
  const [nameEn, setNameEn] = useState(dialog.name_en || '');
  const [subs,   setSubs]   = useState(dialog.subs || []);   // only used on main-add
  const [saving, setSaving] = useState(false);

  const title = isSub
    ? (isEdit ? t('تعديل خدمة فرعية', 'Edit sub-service') : t('إضافة خدمة فرعية', 'Add sub-service'))
    : (isEdit ? t('تعديل خدمة رئيسية', 'Edit main service') : t('إضافة خدمة رئيسية', 'Add main service'));

  const submit = async () => {
    if (!nameAr.trim()) {
      toast.error(t('الاسم بالعربية مطلوب', 'Arabic name is required'));
      return;
    }
    if (!nameEn.trim()) {
      toast.error(t('الاسم بالإنجليزية مطلوب', 'English name is required'));
      return;
    }

    // Main-add extra validation: at least one valid sub
    if (!isSub && !isEdit) {
      if (subs.length < 1) {
        toast.error(t('لازم على الأقل خدمة فرعية واحدة', 'At least one sub-service is required'));
        return;
      }
      for (let i = 0; i < subs.length; i += 1) {
        const s = subs[i];
        if (!s.name_ar.trim()) {
          toast.error(t(`الخدمة الفرعية رقم ${i + 1}: الاسم بالعربية مطلوب`, `Sub-service #${i + 1}: Arabic name required`));
          return;
        }
        if (!s.name_en.trim()) {
          toast.error(t(`الخدمة الفرعية رقم ${i + 1}: الاسم بالإنجليزية مطلوب`, `Sub-service #${i + 1}: English name required`));
          return;
        }
      }
    }

    setSaving(true);
    try {
      let url;
      let method;
      let body;

      if (isSub && isEdit) {
        url = `${API_URL}/api/admin/service-catalog/${scope}/${dialog.main_id}/sub/${dialog.id}`;
        method = 'patch';
        body = { name_ar: nameAr.trim(), name_en: nameEn.trim() };
      } else if (isSub) {
        url = `${API_URL}/api/admin/service-catalog/${scope}/${dialog.main_id}/sub`;
        method = 'post';
        body = { name_ar: nameAr.trim(), name_en: nameEn.trim() };
      } else if (isEdit) {
        url = `${API_URL}/api/admin/service-catalog/${scope}/${dialog.id}`;
        method = 'patch';
        body = { name_ar: nameAr.trim(), name_en: nameEn.trim() };
      } else {
        url = `${API_URL}/api/admin/service-catalog/${scope}`;
        method = 'post';
        body = {
          name_ar: nameAr.trim(),
          name_en: nameEn.trim(),
          sub_services: subs.map((s) => ({
            name_ar: s.name_ar.trim(),
            name_en: s.name_en.trim(),
          })),
        };
      }

      await axios[method](url, body, { headers: getHeaders() });
      toast.success(isEdit ? t('تم الحفظ', 'Saved') : t('تمت الإضافة', 'Added'));
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحفظ', 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                  onClick={onClose}
                  className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40" />
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                  className="fixed inset-0 z-50 flex items-center justify-center p-4"
                  dir={isAr ? 'rtl' : 'ltr'}>
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6" data-testid="service-dialog">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-bold text-white">{title}</h3>
            <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 text-white/60 flex items-center justify-center">
              <X className="w-4 h-4" />
            </button>
          </div>

          {isSub && (
            <div className="mb-4 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/5 text-[12px] text-white/50">
              {t('تحت الخدمة الرئيسية:', 'Under main service:')} <span className="text-white/80">{dialog.main_name}</span>
            </div>
          )}

          <div className="space-y-3">
            <BilingualNamePair
              nameAr={nameAr} setNameAr={setNameAr}
              nameEn={nameEn} setNameEn={setNameEn}
              t={t}
              arTestId="dlg-name-ar" enTestId="dlg-name-en"
            />

            {/* Main-add: inline sub-services list */}
            {!isSub && !isEdit && (
              <div className="pt-3 mt-2 border-t border-white/5">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <div className="text-sm text-white font-semibold">
                      {t('الخدمات الفرعية', 'Sub-services')} <span className="text-red-400">*</span>
                    </div>
                    <div className="text-[11px] text-white/50">
                      {t('إلزامي على الأقل خدمة فرعية واحدة (بدون رسوم — الرسوم بتنضاف داخل نموذج الطلب).',
                         'At least one sub-service is required (no fees — pricing lives inside the request form).')}
                    </div>
                  </div>
                  <button type="button"
                          onClick={() => setSubs((s) => [...s, emptySubDraft()])}
                          className="text-[11px] px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 text-white flex items-center gap-1"
                          data-testid="dlg-add-sub-row">
                    <Plus className="w-3 h-3" />
                    {t('إضافة فرعية', 'Add sub')}
                  </button>
                </div>

                <div className="space-y-2">
                  {subs.map((s, i) => (
                    <SubRowDraft
                      key={i}
                      idx={i}
                      draft={s}
                      canRemove={subs.length > 1}
                      onChange={(next) => setSubs((arr) => arr.map((x, j) => (j === i ? next : x)))}
                      onRemove={() => setSubs((arr) => arr.filter((_, j) => j !== i))}
                      t={t} isAr={isAr}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 flex items-center gap-3 sticky bottom-0 bg-[#0a0a0a] pt-3">
            <Button onClick={onClose} variant="outline"
                    className="border-white/10 bg-transparent text-white h-10 flex-1"
                    data-testid="dlg-cancel">
              {t('إلغاء', 'Cancel')}
            </Button>
            <Button onClick={submit}
                    disabled={saving}
                    className="text-black font-bold h-10 flex-1"
                    style={{ backgroundColor: accent }}
                    data-testid="dlg-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {isEdit ? t('حفظ', 'Save') : t('إضافة', 'Add')}
            </Button>
          </div>
        </div>
      </motion.div>
    </>
  );
}

// ── Bidirectional (Ar↔En) translator pair — the "one button, both ways"
// pattern the user wants applied everywhere from Feb 2026 onwards.
function BilingualNamePair({ nameAr, setNameAr, nameEn, setNameEn, t, arTestId, enTestId }) {
  const [translating, setTranslating] = useState(false);

  const translate = async (direction) => {
    const src = direction === 'ar_to_en' ? nameAr : nameEn;
    if (!src.trim()) {
      toast.error(t('اكتب النص المصدر أولاً', 'Enter the source text first'));
      return;
    }
    setTranslating(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/utils/translate`,
        {
          text: src.trim(),
          source: direction === 'ar_to_en' ? 'ar' : 'en',
          target: direction === 'ar_to_en' ? 'en' : 'ar',
        },
        { headers: getHeaders() },
      );
      const out = r.data?.translated || r.data?.text || '';
      if (!out) {
        toast.error(t('لم أستطع الترجمة', 'Translation failed'));
        return;
      }
      if (direction === 'ar_to_en') setNameEn(out);
      else                          setNameAr(out);
      toast.success(t('تمت الترجمة', 'Translated'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل استدعاء الترجمة', 'Translate call failed'));
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="space-y-2">
      <label className="block">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-white/60">{t('الاسم بالعربية', 'Arabic name')} *</span>
          <button type="button" onClick={() => translate('en_to_ar')}
                  disabled={translating || !nameEn.trim()}
                  className="text-[11px] px-2 py-1 rounded bg-blue-500/10 hover:bg-blue-500/20 text-blue-300 border border-blue-500/30 flex items-center gap-1 disabled:opacity-50"
                  data-testid={`${arTestId}-translate-from-en`}>
            {translating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bot className="w-3 h-3" />}
            {t('ترجمة من الإنجليزية', 'Translate from EN')}
          </button>
        </div>
        <input value={nameAr}
               onChange={(e) => setNameAr(e.target.value)}
               dir="rtl"
               className="w-full h-10 bg-[#050505] border border-white/10 rounded-md px-3 text-white text-sm focus:outline-none focus:border-[#d4af37]/50"
               placeholder={t('مثلاً: طلبات التأشيرة والهوية', 'e.g. Visa & Emirates ID')}
               data-testid={arTestId} />
      </label>

      <div className="flex items-center justify-center py-1">
        <ArrowLeftRight className="w-3 h-3 text-white/20" />
      </div>

      <label className="block">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-white/60">{t('الاسم بالإنجليزية', 'English name')} *</span>
          <button type="button" onClick={() => translate('ar_to_en')}
                  disabled={translating || !nameAr.trim()}
                  className="text-[11px] px-2 py-1 rounded bg-blue-500/10 hover:bg-blue-500/20 text-blue-300 border border-blue-500/30 flex items-center gap-1 disabled:opacity-50"
                  data-testid={`${enTestId}-translate-from-ar`}>
            {translating ? <Loader2 className="w-3 h-3 animate-spin" /> : <Bot className="w-3 h-3" />}
            {t('ترجمة من العربية', 'Translate from AR')}
          </button>
        </div>
        <input value={nameEn}
               onChange={(e) => setNameEn(e.target.value)}
               dir="ltr"
               className="w-full h-10 bg-[#050505] border border-white/10 rounded-md px-3 text-white text-sm focus:outline-none focus:border-[#d4af37]/50"
               placeholder="e.g. Visa & Emirates ID"
               data-testid={enTestId} />
      </label>
    </div>
  );
}

// One row inside the "add main" dialog for entering a sub-service inline.
function SubRowDraft({ idx, draft, canRemove, onChange, onRemove, t, isAr }) {
  const [translating, setTranslating] = useState(false);

  const translate = async (direction) => {
    const src = direction === 'ar_to_en' ? draft.name_ar : draft.name_en;
    if (!src.trim()) {
      toast.error(t('اكتب النص المصدر أولاً', 'Enter the source text first'));
      return;
    }
    setTranslating(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/utils/translate`,
        {
          text: src.trim(),
          source: direction === 'ar_to_en' ? 'ar' : 'en',
          target: direction === 'ar_to_en' ? 'en' : 'ar',
        },
        { headers: getHeaders() },
      );
      const out = r.data?.translated || r.data?.text || '';
      if (!out) return toast.error(t('لم أستطع الترجمة', 'Translation failed'));
      if (direction === 'ar_to_en') onChange({ ...draft, name_en: out });
      else                          onChange({ ...draft, name_ar: out });
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل استدعاء الترجمة', 'Translate call failed'));
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="bg-[#050505] border border-white/10 rounded-lg p-3 space-y-2"
         data-testid={`dlg-sub-row-${idx}`}>
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-white/50">
          {t(`فرعية رقم ${idx + 1}`, `Sub #${idx + 1}`)}
        </span>
        <button type="button"
                onClick={onRemove}
                disabled={!canRemove}
                title={!canRemove ? t('يجب إبقاء خدمة فرعية واحدة على الأقل', 'At least one sub must remain') : ''}
                className={`h-7 w-7 rounded-md flex items-center justify-center ${canRemove ? 'bg-red-500/10 hover:bg-red-500/20 text-red-400' : 'bg-white/[0.03] text-white/20 cursor-not-allowed'}`}
                data-testid={`dlg-sub-remove-${idx}`}>
          <Trash2 className="w-3 h-3" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] text-white/50">{t('عربي', 'AR')} *</span>
            <button type="button" onClick={() => translate('en_to_ar')}
                    disabled={translating || !draft.name_en.trim()}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 hover:bg-blue-500/20 text-blue-300 border border-blue-500/30 disabled:opacity-50 flex items-center gap-1"
                    data-testid={`dlg-sub-ar-translate-${idx}`}>
              {translating ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Bot className="w-2.5 h-2.5" />}
              {t('من EN', 'from EN')}
            </button>
          </div>
          <input value={draft.name_ar}
                 onChange={(e) => onChange({ ...draft, name_ar: e.target.value })}
                 dir="rtl"
                 className="w-full h-9 bg-black border border-white/10 rounded-md px-2 text-white text-sm"
                 placeholder={t('اسم الخدمة الفرعية بالعربية', 'Sub name (AR)')}
                 data-testid={`dlg-sub-name-ar-${idx}`} />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] text-white/50">{t('إنجليزي', 'EN')} *</span>
            <button type="button" onClick={() => translate('ar_to_en')}
                    disabled={translating || !draft.name_ar.trim()}
                    className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 hover:bg-blue-500/20 text-blue-300 border border-blue-500/30 disabled:opacity-50 flex items-center gap-1"
                    data-testid={`dlg-sub-en-translate-${idx}`}>
              {translating ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Bot className="w-2.5 h-2.5" />}
              {t('من AR', 'from AR')}
            </button>
          </div>
          <input value={draft.name_en}
                 onChange={(e) => onChange({ ...draft, name_en: e.target.value })}
                 dir="ltr"
                 className="w-full h-9 bg-black border border-white/10 rounded-md px-2 text-white text-sm"
                 placeholder="Sub name (EN)"
                 data-testid={`dlg-sub-name-en-${idx}`} />
        </div>
      </div>
    </div>
  );
}
